import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Plus, Minus, ShoppingCart, ShieldCheck, Snowflake, FlaskConical, Check, Image as ImageIcon, Atom, AlertCircle, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, imgUrl, BACKEND_URL } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import SequenceMolecule3D from "@/components/SequenceMolecule3D";

export default function ProductDetail() {
    const { slug } = useParams();
    const { t } = useTranslation();
    const [product, setProduct] = useState(null);
    const [qty, setQty] = useState(1);
    const [tab, setTab] = useState("overview");
    const [selectedDose, setSelectedDose] = useState(null);
    const [view, setView] = useState("photo"); // "photo" | "3d"
    const { addItem } = useCart();

    useEffect(() => {
        api.get(`/products/${slug}`)
            .then((r) => {
                setProduct(r.data);
                // Default to the smallest available dose
                if (r.data?.pricing) {
                    const cheapest = Object.entries(r.data.pricing).sort((a, b) => a[1] - b[1])[0];
                    if (cheapest) setSelectedDose(cheapest[0]);
                }
            })
            .catch(() => setProduct(false));
    }, [slug]);

    if (product === null) {
        return (
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 text-neutral-500">{t("common.loading")}</div>
        );
    }
    if (product === false) {
        return (
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">
                <h1 className="font-display text-4xl font-bold">{t("product.notFound")}</h1>
                <Link to="/shop" className="mt-4 inline-block text-[#0077b6]">{t("product.backToShop")}</Link>
            </div>
        );
    }

    return (
        <div data-testid="product-detail-page" className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 py-10 sm:py-16 lg:py-24">
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-neutral-500 mb-6 sm:mb-8 truncate">
                <Link to="/" className="hover:text-black">{t("product.crumbHome")}</Link>
                <span className="mx-2">/</span>
                <Link to="/shop" className="hover:text-black">{t("product.crumbShop")}</Link>
                <span className="mx-2">/</span>
                <span className="text-neutral-700">{product.name}</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20">
                <div className="relative">
                    {/* Photo ↔ 3D toggle */}
                    <div className="flex items-center gap-2 mb-3" data-testid="media-toggle">
                        <button
                            type="button"
                            onClick={() => setView("photo")}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold border rounded-lg transition-colors ${
                                view === "photo"
                                    ? "bg-[#0089c8] text-white border-[#0089c8]"
                                    : "border-neutral-200 text-neutral-600 hover:border-[#0089c8] hover:text-black"
                            }`}
                            data-testid="media-toggle-photo"
                        >
                            <ImageIcon className="w-3 h-3" /> {t("product.viewPhoto")}
                        </button>
                        <button
                            type="button"
                            onClick={() => setView("3d")}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold border rounded-lg transition-colors ${
                                view === "3d"
                                    ? "bg-[#0089c8] text-white border-[#0089c8]"
                                    : "border-neutral-200 text-neutral-600 hover:border-[#0089c8] hover:text-black"
                            }`}
                            data-testid="media-toggle-3d"
                        >
                            <Atom className="w-3 h-3" /> {t("product.view3D")}
                        </button>
                    </div>

                    <div className="aspect-square bg-white border border-neutral-200 overflow-hidden relative rounded-2xl">
                        {view === "photo" ? (
                            <>
                                <img
                                    src={imgUrl(product.image)}
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                    data-testid="product-image"
                                    onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.classList.add("nx-image-fallback"); }}
                                />
                                <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.22em] text-[#0077b6] border border-[#00aeef]/40 px-2 py-1 bg-white/60 rounded-lg">
                                    {product.purity} {t("product.hplc")}
                                </div>
                            </>
                        ) : product.sequence ? (
                            <div className="absolute inset-0" data-testid="product-3d-viewer">
                                <SequenceMolecule3D sequence={product.sequence} height={580} />
                                <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.22em] text-[#0077b6] border border-[#00aeef]/40 px-2 py-1 bg-white/60 rounded-lg pointer-events-none">
                                    {product.sequence.length} {t("product.residues")}
                                </div>
                                <div className="absolute bottom-4 right-4 text-[10px] uppercase tracking-[0.22em] text-neutral-500 font-mono pointer-events-none">
                                    {t("product.dragRotate")}
                                </div>
                            </div>
                        ) : (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-testid="product-3d-unavailable">
                                <AlertCircle className="w-8 h-8 text-neutral-500 mb-3" />
                                <div className="text-[10px] uppercase tracking-[0.22em] text-[#0077b6] mb-2">{t("product.structureUnavailableEyebrow")}</div>
                                <div className="font-display text-lg font-bold text-neutral-800 mb-2">{t("product.structureUnavailableTitle")}</div>
                                <p className="text-xs text-neutral-500 leading-relaxed max-w-xs">{t("product.structureUnavailableBody")}</p>
                            </div>
                        )}
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-4 text-center">
                        {[
                            { label: t("product.purity"), value: product.purity },
                            { label: t("product.doses"), value: product.pricing ? t("product.sizes", { count: Object.keys(product.pricing).length }) : product.dosage?.split(",")[0] || "—" },
                            { label: t("product.classLabel"), value: product.category?.split(/[ /]/)[0] || "—" },
                        ].map((s) => (
                            <div key={s.label} className="border border-neutral-200 py-3 sm:py-4 rounded-lg">
                                <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-neutral-500">{s.label}</div>
                                <div className="font-mono text-xs sm:text-sm text-[#0077b6] mt-1 break-words">{s.value}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3">{product.category}</div>
                    <h1 className="font-display text-3xl sm:text-4xl lg:text-6xl font-extrabold uppercase tracking-tighter break-words" data-testid="product-title">
                        {product.name}
                    </h1>
                    <div className="text-neutral-600 text-sm sm:text-base mt-2">{product.short_name}</div>

                    <div className="mt-6 sm:mt-8 flex items-baseline gap-3 sm:gap-4 flex-wrap">
                        <div className="font-display text-4xl sm:text-5xl font-extrabold text-neutral-900" data-testid="product-price">
                            £{(selectedDose && product.pricing?.[selectedDose] != null
                                ? product.pricing[selectedDose]
                                : product.price
                            ).toFixed(2)}
                        </div>
                        {typeof product.compare_at_price === "number" && product.compare_at_price > product.price && (
                            <>
                                <div
                                    className="text-lg sm:text-xl text-neutral-400 line-through"
                                    data-testid="product-compare-at"
                                >
                                    £{product.compare_at_price.toFixed(2)}
                                </div>
                                <div
                                    className="text-[10px] uppercase tracking-[0.22em] text-white bg-[#e11d48] px-2 py-1 font-bold rounded-lg self-center"
                                    data-testid="product-sale-badge"
                                >
                                    −{Math.round((1 - product.price / product.compare_at_price) * 100)}%
                                </div>
                            </>
                        )}
                        <div className="text-xs uppercase tracking-[0.18em] text-neutral-500">
                            {selectedDose ? t("product.vialOf", { dose: selectedDose }) : t("product.perVial")}
                        </div>
                    </div>

                    {/* What this peptide does — pulled from the catalog */}
                    <div className="mt-6 sm:mt-8 border-l-2 border-[#00aeef] bg-[#f8f8f8]/[0.04] pl-4 sm:pl-5 pr-3 sm:pr-4 py-4 rounded-r-lg" data-testid="product-description">
                        <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-2">{t("product.whatItDoes")}</div>
                        <p className="text-neutral-800 leading-relaxed text-sm sm:text-[15px]">{product.description}</p>
                    </div>

                    {/* Available sizes / dose pricing — clickable */}
                    {product.pricing && Object.keys(product.pricing).length > 0 && (
                        <div className="mt-6">
                            <div className="text-[10px] uppercase tracking-[0.28em] text-neutral-500 mb-3">
                                {t("product.selectSize")} {selectedDose && <span className="text-[#0077b6] ml-1">· {selectedDose}</span>}
                            </div>
                            <div className="flex flex-wrap gap-2" data-testid="dose-options">
                                {Object.entries(product.pricing).map(([dose, price]) => {
                                    const isActive = selectedDose === dose;
                                    return (
                                        <button
                                            type="button"
                                            key={dose}
                                            onClick={() => setSelectedDose(dose)}
                                            className={`px-3 py-2 rounded-lg text-xs border transition-colors ${
                                                isActive
                                                    ? "border-[#00aeef] bg-[#00aeef]/[0.06] ring-1 ring-[#00aeef]/40"
                                                    : "border-neutral-200 hover:border-[#0089c8]/60 bg-transparent"
                                            }`}
                                            data-testid={`dose-${dose}`}
                                            aria-pressed={isActive}
                                        >
                                            <span className={`font-mono ${isActive ? "text-neutral-900" : "text-neutral-700"}`}>{dose}</span>
                                            <span className={`ml-2 font-bold ${isActive ? "text-[#0077b6]" : "text-neutral-600"}`}>
                                                £{price.toFixed(2)}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Safety notes from catalog */}
                    {product.notes && (
                        <div className="mt-6 border border-amber-500/30 bg-amber-500/[0.05] p-4 rounded-lg" data-testid="product-notes">
                            <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 mb-2 flex items-center gap-2">
                                <ShieldCheck className="w-3 h-3" /> {t("product.researchAdvisory")}
                            </div>
                            <p className="text-neutral-700 leading-relaxed text-sm">{product.notes}</p>
                        </div>
                    )}

                    <div className="mt-6 sm:mt-8 flex items-stretch gap-3 sm:gap-4">
                        <div className="flex items-center border border-neutral-200 rounded-lg flex-shrink-0">
                            <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:text-black" data-testid="qty-dec">
                                <Minus className="w-4 h-4" />
                            </button>
                            <span className="px-4 sm:px-6 font-mono text-sm" data-testid="qty-value">{qty}</span>
                            <button onClick={() => setQty(qty + 1)} className="p-3 hover:text-black" data-testid="qty-inc">
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                        <button
                            onClick={() => addItem(product, qty, selectedDose)}
                            className="flex-1 inline-flex items-center justify-center gap-2 sm:gap-3 bg-[#0089c8] hover:bg-[#0077b6] rounded-lg text-white font-bold uppercase tracking-[0.14em] sm:tracking-[0.18em] text-[10px] sm:text-xs px-2 sm:px-4 py-3 sm:py-4 transition-colors text-center"
                            data-testid="add-to-cart-detail-btn"
                        >
                            <ShoppingCart className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">
                                {t("product.addToCart")} {selectedDose ? `${selectedDose} ` : ""}· £{(
                                    (selectedDose && product.pricing?.[selectedDose] != null
                                        ? product.pricing[selectedDose]
                                        : product.price) * qty
                                ).toFixed(2)}
                            </span>
                        </button>
                    </div>

                    <div className="mt-6 sm:mt-8 grid grid-cols-3 gap-2 sm:gap-3 text-[9px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-neutral-600">
                        <a
                            href={`${BACKEND_URL}/api/products/${product.slug}/coa`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 sm:gap-2 border border-neutral-200 p-2 sm:p-3 rounded-lg hover:border-[#0089c8] hover:text-[#0077b6] transition-colors group"
                            data-testid="coa-chip"
                            title={t("product.downloadCoa")}
                        >
                            <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#0077b6] flex-shrink-0" />
                            <span className="truncate">{t("product.coaAvailable")}</span>
                        </a>
                        <div className="flex items-center gap-1.5 sm:gap-2 border border-neutral-200 p-2 sm:p-3 rounded-lg"><Snowflake className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#0077b6] flex-shrink-0" /> <span className="truncate">{t("product.coldChain")}</span></div>
                        <div className="flex items-center gap-1.5 sm:gap-2 border border-neutral-200 p-2 sm:p-3 rounded-lg"><FlaskConical className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#0077b6] flex-shrink-0" /> <span className="truncate">{t("product.researchOnly")}</span></div>
                    </div>

                    <a
                        href={`${BACKEND_URL}/api/products/${product.slug}/coa`}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={`COA-${product.slug}.pdf`}
                        className="mt-4 inline-flex items-center gap-3 border border-[#00aeef]/40 bg-[#00aeef]/[0.06] hover:bg-[#00aeef]/[0.12] text-[#0077b6] hover:text-[#005f92] font-bold uppercase tracking-[0.18em] text-xs px-5 py-3.5 rounded-lg transition-colors w-full sm:w-auto justify-center"
                        data-testid="download-coa-btn"
                    >
                        <Download className="w-4 h-4" />
                        {t("product.downloadCoa")}
                    </a>

                    <div className="mt-10 sm:mt-12 border-t border-neutral-200 pt-6">
                        <div className="flex gap-1 mb-6 overflow-x-auto -mx-1 px-1 scrollbar-hide">
                            {[
                                { key: "overview", label: t("product.tabOverview") },
                                { key: "specs", label: t("product.tabSpecs") },
                                { key: "storage", label: t("product.tabStorage") },
                            ].map((tabItem) => (
                                <button
                                    key={tabItem.key}
                                    onClick={() => setTab(tabItem.key)}
                                    className={`flex-shrink-0 px-3 sm:px-4 py-2 text-xs uppercase tracking-[0.18em] sm:tracking-[0.22em] font-semibold ${
                                        tab === tabItem.key ? "text-[#0077b6] border-b-2 border-[#00aeef]" : "text-neutral-500 hover:text-neutral-900"
                                    }`}
                                    data-testid={`tab-${tabItem.key}`}
                                >
                                    {tabItem.label}
                                </button>
                            ))}
                        </div>
                        {tab === "overview" && (
                            <div className="space-y-3">
                                <div className="text-sm text-neutral-700 font-semibold">{t("product.highlights")}</div>
                                {product.benefits.map((b) => (
                                    <div key={b} className="flex items-start gap-3 text-sm text-neutral-600">
                                        <Check className="w-4 h-4 text-[#0077b6] mt-0.5 flex-shrink-0" />
                                        {b}
                                    </div>
                                ))}
                            </div>
                        )}
                        {tab === "specs" && (
                            <div className="space-y-3 font-mono text-xs">
                                <div className="flex justify-between gap-3 border-b border-neutral-200 py-3"><span className="text-neutral-500 uppercase tracking-[0.18em] flex-shrink-0">{t("product.sequence")}</span><span className="text-neutral-800 text-right break-all">{product.sequence || "—"}</span></div>
                                <div className="flex justify-between gap-3 border-b border-neutral-200 py-3"><span className="text-neutral-500 uppercase tracking-[0.18em] flex-shrink-0">{t("product.molecularWeight")}</span><span className="text-neutral-800 text-right">{product.molecular_weight}</span></div>
                                <div className="flex justify-between gap-3 border-b border-neutral-200 py-3"><span className="text-neutral-500 uppercase tracking-[0.18em] flex-shrink-0">{t("product.purity")}</span><span className="text-[#0077b6] text-right">{product.purity}</span></div>
                                <div className="flex justify-between gap-3 border-b border-neutral-200 py-3"><span className="text-neutral-500 uppercase tracking-[0.18em] flex-shrink-0">{t("product.format")}</span><span className="text-neutral-800 text-right break-words">{product.dosage}</span></div>
                            </div>
                        )}
                        {tab === "storage" && (
                            <div className="text-sm text-neutral-600 leading-relaxed">
                                {product.storage} {t("product.storageBody")}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
